import { useState, useEffect, useCallback } from 'react';
import zonesService from '../services/zones';

/**
 * Hook personalizado para gerir zonas
 * @param {number} idempresa - ID da empresa
 * @returns {Object} Estado e funções para gerir zonas
 */
export const useZones = (idempresa) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Carregar zonas da API
   */
  const loadZones = useCallback(async () => {
    if (!idempresa) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.getZones(idempresa);
      
      if (response.status === 'success') {
        const zones = response.zones || [];
        // Calcular área para zonas que não têm área calculada
        const zonesWithArea = zones.map(zone => {
          if (!zone.area && zone.coordinates && zone.coordinates.length >= 3) {
            // Função para calcular área (copiada do componente principal)
            const calculatePolygonArea = (coordinates) => {
              if (!coordinates || coordinates.length < 3) return 0;
              
              let area = 0;
              const n = coordinates.length - 1;
              
              for (let i = 0; i < n; i++) {
                const j = (i + 1) % n;
                area += coordinates[i][1] * coordinates[j][0];
                area -= coordinates[j][1] * coordinates[i][0];
              }
              
              const areaInDegrees = Math.abs(area) / 2;
              
              if (areaInDegrees < 1e-10) return 0;
              
              let latSum = 0;
              for (let i = 0; i < n; i++) {
                latSum += coordinates[i][0];
              }
              const avgLat = latSum / n;
              const latRad = avgLat * Math.PI / 180;
              
              const kmPerDegreeLat = 111.32;
              const kmPerDegreeLng = 111.32 * Math.cos(latRad);
              
              return areaInDegrees * kmPerDegreeLat * kmPerDegreeLng;
            };
            
            return {
              ...zone,
              area: calculatePolygonArea(zone.coordinates)
            };
          }
          return zone;
        });
        
        setZones(zonesWithArea);
      } else {
        throw new Error(response.message || 'Erro ao carregar zonas');
      }
    } catch (err) {
      console.error('Erro ao carregar zonas:', err);
      setError(err.message);
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [idempresa]);

  /**
   * Adicionar nova zona
   * @param {Object} zoneData - Dados da zona
   */
  const addZone = useCallback(async (zoneData) => {
    if (!idempresa) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.saveZone({
        ...zoneData,
        idempresa
      });

      if (response.status === 'success') {
        // Recarregar zonas após adicionar
        await loadZones();
        return response;
      } else {
        throw new Error(response.message || 'Erro ao guardar zona');
      }
    } catch (err) {
      console.error('Erro ao adicionar zona:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [idempresa, loadZones]);

  /**
   * Atualizar zona existente
   * @param {Object} zoneData - Dados da zona
   */
  const updateZone = useCallback(async (zoneData) => {
    if (!idempresa) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.updateZone({
        ...zoneData,
        idempresa
      });

      if (response.status === 'success') {
        // Atualizar zona localmente
        setZones(prevZones => 
          prevZones.map(zone => 
            zone.id === zoneData.id ? { ...zone, ...zoneData } : zone
          )
        );
        return response;
      } else {
        throw new Error(response.message || 'Erro ao atualizar zona');
      }
    } catch (err) {
      console.error('Erro ao atualizar zona:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [idempresa]);

  /**
   * Eliminar zona
   * @param {number} zoneId - ID da zona
   */
  const deleteZone = useCallback(async (zoneId) => {
    if (!idempresa) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.deleteZone(idempresa, zoneId);

      if (response.status === 'success') {
        // Remover zona localmente
        setZones(prevZones => prevZones.filter(zone => zone.id !== zoneId));
        return response;
      } else {
        throw new Error(response.message || 'Erro ao eliminar zona');
      }
    } catch (err) {
      console.error('Erro ao eliminar zona:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [idempresa]);

  /**
   * Eliminar múltiplas zonas
   * @param {Array} zoneIds - Array de IDs das zonas
   */
  const deleteMultipleZones = useCallback(async (zoneIds) => {
    if (!idempresa || !zoneIds.length) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.deleteMultipleZones(idempresa, zoneIds);

      if (response.status === 'success') {
        // Remover zonas localmente
        setZones(prevZones => 
          prevZones.filter(zone => !zoneIds.includes(zone.id))
        );
        return response;
      } else {
        throw new Error(response.message || 'Erro ao eliminar zonas');
      }
    } catch (err) {
      console.error('Erro ao eliminar múltiplas zonas:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [idempresa]);

  /**
   * Sincronizar zona local com a base de dados
   * @param {Object} localZone - Zona criada localmente
   */
  const syncZone = useCallback(async (localZone) => {
    try {
      // Adicionar à base de dados
      await addZone(localZone);
    } catch (err) {
      console.error('Erro ao sincronizar zona:', err);
      // Manter zona local mesmo se falhar a sincronização
    }
  }, [addZone]);

  /**
   * Sincronizar múltiplas zonas com a base de dados
   * @param {Array} localZones - Array de zonas criadas localmente
   */
  const syncMultipleZones = useCallback(async (localZones) => {
    if (!idempresa || !localZones.length) return;

    setLoading(true);
    setError(null);

    try {
      const response = await zonesService.saveMultipleZones(idempresa, localZones);
      
      if (response.status === 'success') {
        // Recarregar zonas após sincronizar
        await loadZones();
        return response;
      } else {
        throw new Error(response.message || 'Erro ao sincronizar zonas');
      }
    } catch (err) {
      console.error('Erro ao sincronizar múltiplas zonas:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [idempresa, loadZones]);

  // Carregar zonas quando o idempresa mudar
  useEffect(() => {
    if (idempresa && idempresa > 0) {
      loadZones();
    }
  }, [idempresa, loadZones]);

  return {
    zones,
    loading,
    error,
    loadZones,
    addZone,
    updateZone,
    deleteZone,
    deleteMultipleZones,
    syncZone,
    syncMultipleZones
  };
};

export default useZones;
